import { useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Plus, Trash } from "@phosphor-icons/react";
import AutocompleteInput from "@/components/AutocompleteInput";
import EnhancedPhotoUploader from "@/components/EnhancedPhotoUploader";

function FormField({ label, value, onChange, type = "text", placeholder = "", className = "", as = "input" }) {
  const Component = as === "textarea" ? Textarea : Input;
  return (
    <div className={className}>
      <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 mb-1.5 block">{label}</Label>
      <Component type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
    </div>
  );
}

const CHECKLIST_ITEMS = [
  { key: "cargo_checked", label: "Cargo condition checked before transloading" },
  { key: "container_checked", label: "Container/truck condition checked before loading" },
  { key: "cargo_counted", label: "Cargo counted during transfer" },
  { key: "damaged_separated", label: "Damaged cargo separated" },
  { key: "photos_taken", label: "Photos taken before, during, and after operation" },
  { key: "new_seal_applied", label: "New seal applied" },
  { key: "instructions_followed", label: "Customer instructions followed" },
  { key: "exceptions_recorded", label: "Exceptions recorded" },
];

const PHOTO_CATEGORIES = [
  "Before unloading", "Seal / container number", "Cargo condition before transfer",
  "During transloading", "Damaged / exception cargo", "Cargo loaded in new unit",
  "Final stowage", "New seal", "Other"
];

const ACTIVITY_SUGGESTIONS = [
  "Arrival", "Seal checked", "Doors opened", "Cargo unloaded",
  "Cargo inspected", "Cargo reloaded", "New seal applied", "Operation completed"
];

export default function TransloadingForm({ data, onChange }) {
  const checklist = data.checklist || {};
  const timeline = data.operation_timeline || [];

  const toggleCheck = (key) => {
    onChange("checklist", { ...checklist, [key]: !checklist[key] });
  };

  const addTimelineRow = () => {
    onChange("operation_timeline", [...timeline, { time: "", activity: "", responsible_person: "", notes: "" }]);
  };

  const updateTimelineRow = (idx, field, value) => {
    const updated = [...timeline];
    updated[idx] = { ...updated[idx], [field]: value };
    onChange("operation_timeline", updated);
  };

  const removeTimelineRow = (idx) => {
    onChange("operation_timeline", timeline.filter((_, i) => i !== idx));
  };

  return (
    <div className="space-y-6" data-testid="transloading-form">
      {/* Header */}
      <div className="bg-white border border-slate-200 rounded-md p-6">
        <h3 className="text-lg font-bold tracking-tight text-slate-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>Header Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <AutocompleteInput label="Applicant / Customer" value={data.applicant} onChange={v => onChange("applicant", v)} endpoint="applicants" testId="applicant-input" placeholder="Start typing..." />
          <FormField label="Our Reference Number" value={data.reference_number} onChange={v => onChange("reference_number", v)} />
          <FormField label="Customer Reference Number" value={data.customer_reference_number} onChange={v => onChange("customer_reference_number", v)} />
          <FormField label="Date of Operation" value={data.date_of_operation} onChange={v => onChange("date_of_operation", v)} type="date" />
          <FormField label="Place of Operation" value={data.place_of_operation} onChange={v => onChange("place_of_operation", v)} />
          <AutocompleteInput label="Operator / Surveyor" value={data.surveyor} onChange={v => onChange("surveyor", v)} endpoint="surveyors" testId="surveyor-input" placeholder="Start typing..." />
        </div>
      </div>

      {/* Shipment Details */}
      <div className="bg-white border border-slate-200 rounded-md p-6">
        <h3 className="text-lg font-bold tracking-tight text-slate-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>Shipment / Operation Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField label="Shipper" value={data.shipper} onChange={v => onChange("shipper", v)} />
          <FormField label="Consignee / Notify Party" value={data.consignee} onChange={v => onChange("consignee", v)} />
          <FormField label="Port of Loading" value={data.port_of_loading} onChange={v => onChange("port_of_loading", v)} />
          <FormField label="Place of Delivery" value={data.place_of_delivery} onChange={v => onChange("place_of_delivery", v)} />
          <FormField label="Cargo Description" value={data.cargo_description} onChange={v => onChange("cargo_description", v)} className="md:col-span-2" />
          <FormField label="Total Gross Weight" value={data.total_gross_weight} onChange={v => onChange("total_gross_weight", v)} />
          <FormField label="Number of Packages / Pallets" value={data.number_of_packages} onChange={v => onChange("number_of_packages", v)} />
          <FormField label="Origin Container / Truck Number" value={data.origin_container} onChange={v => onChange("origin_container", v)} />
          <FormField label="Destination Container / Truck Number" value={data.destination_container} onChange={v => onChange("destination_container", v)} />
          <FormField label="Origin Seal Number" value={data.origin_seal} onChange={v => onChange("origin_seal", v)} />
          <FormField label="New Seal Number" value={data.new_seal} onChange={v => onChange("new_seal", v)} />
          <FormField label="Equipment Used" value={data.equipment_used} onChange={v => onChange("equipment_used", v)} />
          <FormField label="Warehouse / Dock / Bay Number" value={data.warehouse_number} onChange={v => onChange("warehouse_number", v)} />
        </div>
      </div>

      {/* Checklist */}
      <div className="bg-white border border-slate-200 rounded-md p-6">
        <h3 className="text-lg font-bold tracking-tight text-slate-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>Transloading Checklist</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {CHECKLIST_ITEMS.map(item => (
            <label key={item.key} className="flex items-center gap-2.5 text-sm text-slate-700 cursor-pointer hover:text-slate-900">
              <Checkbox checked={checklist[item.key] || false} onCheckedChange={() => toggleCheck(item.key)} data-testid={`checklist-${item.key}`} />
              {item.label}
            </label>
          ))}
        </div>
      </div>

      {/* Operation Timeline */}
      <div className="bg-white border border-slate-200 rounded-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Operation Timeline</h3>
          <Button type="button" onClick={addTimelineRow} className="bg-orange-500 hover:bg-orange-600 text-white gap-2 text-sm" data-testid="add-timeline-row">
            <Plus size={14} weight="bold" /> Add Entry
          </Button>
        </div>
        {timeline.length > 0 ? (
          <div className="border border-slate-200 rounded-md overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b">
                  <th className="text-left text-xs font-semibold uppercase text-slate-500 px-3 py-2 w-24">Time</th>
                  <th className="text-left text-xs font-semibold uppercase text-slate-500 px-3 py-2">Activity</th>
                  <th className="text-left text-xs font-semibold uppercase text-slate-500 px-3 py-2 w-36">Responsible</th>
                  <th className="text-left text-xs font-semibold uppercase text-slate-500 px-3 py-2">Notes</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody>
                {timeline.map((row, i) => (
                  <tr key={i} className="border-b border-slate-100" data-testid={`timeline-row-${i}`}>
                    <td className="px-2 py-1.5"><Input value={row.time || ""} onChange={e => updateTimelineRow(i, "time", e.target.value)} className="h-8 text-sm border-slate-200" placeholder="08:00" /></td>
                    <td className="px-2 py-1.5"><Input value={row.activity || ""} onChange={e => updateTimelineRow(i, "activity", e.target.value)} className="h-8 text-sm border-slate-200" placeholder="Activity" list={`activity-list-${i}`} />
                      <datalist id={`activity-list-${i}`}>{ACTIVITY_SUGGESTIONS.map(a => <option key={a} value={a} />)}</datalist>
                    </td>
                    <td className="px-2 py-1.5"><Input value={row.responsible_person || ""} onChange={e => updateTimelineRow(i, "responsible_person", e.target.value)} className="h-8 text-sm border-slate-200" placeholder="Name" /></td>
                    <td className="px-2 py-1.5"><Input value={row.notes || ""} onChange={e => updateTimelineRow(i, "notes", e.target.value)} className="h-8 text-sm border-slate-200" placeholder="Notes" /></td>
                    <td className="px-2 py-1.5"><Button variant="ghost" size="sm" onClick={() => removeTimelineRow(i)} className="text-red-500 hover:bg-red-50 h-8 w-8 p-0"><Trash size={14} /></Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="border border-dashed border-slate-300 rounded-md p-6 text-center text-slate-400 text-sm">
            No timeline entries. Click "Add Entry" to start recording the operation.
          </div>
        )}
      </div>

      {/* Photos */}
      <div className="bg-white border border-slate-200 rounded-md p-6">
        <h3 className="text-lg font-bold tracking-tight text-slate-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>Photo Documentation</h3>
        <EnhancedPhotoUploader
          entries={data.photo_entries || []}
          onChange={v => onChange("photo_entries", v)}
          categories={PHOTO_CATEGORIES}
        />
      </div>

      {/* Findings */}
      <div className="bg-white border border-slate-200 rounded-md p-6">
        <h3 className="text-lg font-bold tracking-tight text-slate-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>Findings & Observations</h3>
        <div className="space-y-4">
          <FormField label="Survey Findings" value={data.survey_findings} onChange={v => onChange("survey_findings", v)} as="textarea" />
          <FormField label="Observations" value={data.observations} onChange={v => onChange("observations", v)} as="textarea" />
          <FormField label="Exceptions" value={data.exceptions} onChange={v => onChange("exceptions", v)} as="textarea" />
          <FormField label="Recommendation / Next Action" value={data.recommendation} onChange={v => onChange("recommendation", v)} as="textarea" />
          <FormField label="Disclaimer" value={data.disclaimer} onChange={v => onChange("disclaimer", v)} as="textarea" />
        </div>
      </div>
    </div>
  );
}
